import { createColumnHelper } from '@tanstack/react-table';
import clsx from 'clsx';
import React, { useCallback } from 'react';
import {
  Control,
  Controller,
  FieldErrorsImpl,
  UseFormRegister,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Tooltip from 'react-tooltip-lite';
import { themeColors } from 'utils/tailwind/tailwindHelpers';
import * as yup from 'yup';

import { IAI } from '@/modules/AiAgents/types';
import { InputField, SelectField } from '@/shared/components/atoms';
import { allPropertyFields } from '@/shared/constant/general';

import InfoCircle from '~/images/shared/ExleminationMark.svg';

const useAIChatBot = () => {
  const { t } = useTranslation('common');

  const chatValidationSchema: yup.SchemaOf<{
    message?: string;
    file?: File;
  }> = yup.object().shape({
    message: yup.string(),
    file: yup.mixed().nullable(),
  });

  const fieldsTableValidationSchema: yup.SchemaOf<IAI.IFieldsFormSchema> = yup
    .object()
    .shape({
      fields: yup
        .array()
        .of(
          yup.object().shape({
            id: yup.number().required(t('this-field-is-required')),
            name: yup.string().required(t('this-field-is-required')),
            area: yup
              .number()
              .positive(
                t('field-greater-than-val', {
                  val: 0,
                })
              )
              .required(t('this-field-is-required')),
            type: yup.string().required(t('this-field-is-required')),
            crop: yup.string().required(t('this-field-is-required')),
            cropId: yup.number().optional(),
          })
        )
        .min(
          1,
          t('field-greater-than-val', {
            val: 1,
          })
        ),
    });

  const getFieldsTableColumn = useCallback(
    (
      isEditing: boolean,
      control: Control<IAI.IFieldsFormSchema>,
      register: UseFormRegister<IAI.IFieldsFormSchema>,
      errors: Partial<FieldErrorsImpl<IAI.IFieldsFormSchema>>
    ) => {
      const columnHelper = createColumnHelper<
        IAI.IAIFieldProps & { error?: boolean }
      >();

      return !isEditing
        ? [
            columnHelper.accessor('id', {
              header: t('n'),
              cell: ({ row: { index } }) => index + 1,
            }),
            columnHelper.accessor('name', {
              header: t('field-name'),
              cell: ({
                getValue,
                row: {
                  original: { hasExistingDuplicate, hasSessionDuplicate },
                },
              }) => {
                return (
                  <div className='flex items-center gap-8'>
                    <span
                      className={clsx(
                        (hasExistingDuplicate || hasSessionDuplicate) &&
                          'text-error-400'
                      )}
                    >
                      {getValue()}
                    </span>
                    {hasExistingDuplicate || hasSessionDuplicate ? (
                      <Tooltip
                        content={
                          <div className='rounded-md px-2 py-1 text-12 shadow-10'>
                            {t(
                              hasExistingDuplicate
                                ? 'old-data-duplicate'
                                : 'ai-duplicate'
                            )}
                          </div>
                        }
                        background={themeColors().background['neutral-99']}
                        className='react-tooltip-rounded'
                        arrow
                        arrowSize={10}
                        direction='bottom'
                        tagName='div'
                        tipContentHover
                        padding={0}
                      >
                        <span className='cursor-pointer text-error-400'>
                          <InfoCircle width='16' height='16' />
                        </span>
                      </Tooltip>
                    ) : (
                      ''
                    )}
                  </div>
                );
              },
            }),
            columnHelper.accessor('crop', {
              header: t('crop-type'),
              cell: ({ getValue }) => (
                <span className='text-surface-10'>{getValue() ?? '-'}</span>
              ),
            }),
            columnHelper.accessor('area', {
              header: t('area'),
              cell: ({ getValue }) => (
                <span className='text-surface-10'>{getValue() ?? '-'}</span>
              ),
            }),
            columnHelper.accessor('type', {
              header: t('Field Type'),
              cell: ({ getValue }) => (
                <span className='text-surface-10'>{getValue() ?? '-'}</span>
              ),
            }),
          ]
        : [
            columnHelper.accessor('id', {
              header: t('n'),
              cell: ({ row: { index } }) => index + 1,
            }),
            columnHelper.accessor('name', {
              header: t('field-name'),
              cell: ({ row: { original, index } }) => (
                <Controller
                  name={`fields.${index}.name`}
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      id={`fields.${index}.name`}
                      py='!py-14'
                      borderColor={
                        original.error
                          ? 'border-error-400'
                          : 'border-outline-variant'
                      }
                      // {...register(`fields.${index}.name`, {
                      //   setValueAs: (value) => value,
                      // })}
                      // defaultValue={getValue()}
                      autoComplete='off'
                      placeholder={t('field-name')}
                      error={!!errors?.fields?.[index]?.name || original.error}
                      type='text'
                      required
                    />
                  )}
                />
              ),
            }),
            columnHelper.accessor('crop', {
              header: t('crop-type'),
              cell: ({ row: { index } }) => (
                <Controller
                  name={`fields.${index}.crop`}
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      id={`fields.${index}.crop`}
                      py='!py-14'
                      borderColor='border-outline-variant'
                      // {...register(`fields.${index}.crop`, {
                      //   setValueAs: (value) => value,
                      // })}
                      // defaultValue={getValue()}
                      autoComplete='off'
                      placeholder={t('field-crop')}
                      error={!!errors?.fields?.[index]?.crop}
                      type='text'
                      required
                    />
                  )}
                />
              ),
            }),
            columnHelper.accessor('area', {
              header: t('area'),
              cell: ({ row: { index } }) => (
                <Controller
                  name={`fields.${index}.area`}
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      id={`fields.${index}.area`}
                      py='!py-14'
                      borderColor='border-outline-variant'
                      // {...register(`fields.${index}.area`, {
                      //   setValueAs: (value) =>
                      //     value ? Number(value) : undefined,
                      // })}
                      // defaultValue={getValue()}
                      autoComplete='off'
                      placeholder={t('area')}
                      error={!!errors?.fields?.[index]?.area}
                      type='number'
                      required
                    />
                  )}
                />
              ),
            }),
            columnHelper.accessor('type', {
              header: t('Field Type'),
              cell: ({ row: { index } }) => (
                <SelectField
                  id={`fields.${index}.type`}
                  borderColor={themeColors().outline.variant}
                  className='!bg-white'
                  py='0.5rem'
                  px='0.5rem'
                  borderRadius='1rem'
                  isSearchable
                  // defaultValue={getValue()}
                  getLabel={(item: { label: string }) => item.label}
                  getValue={(item: { value: string }) => item.value}
                  required
                  list={allPropertyFields.map((field) => ({
                    label: field,
                    value: field,
                  }))}
                  placeholder={t('Field Type')}
                />
              ),
            }),
          ];
    },
    [t]
  );

  return {
    chatValidationSchema,
    getFieldsTableColumn,
    fieldsTableValidationSchema,
  };
};
export default useAIChatBot;
